import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, WandSparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { lmError } from './lmApi';
import { GhostButton, PrimaryButton } from './LmBits';
import { labelOf } from './lmColumns';

/**
 * AI enrichment of STAGED values only (prototype "Enrich" in Staging).
 * Suggestions fill empty allowlisted fields; nothing reaches the CRM until
 * the row is pooled and an Admin commits it.
 */
export default function LmEnrichDialog({ open, onClose, api, rowIds, labelFor, fieldLabels, onApplied }) {
    const [phase, setPhase] = useState('loading');
    const [suggestions, setSuggestions] = useState([]);
    const [picked, setPicked] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setPhase('loading'); setError(null); setSuggestions([]); setPicked({});
        api.enrichPreview({ row_ids: rowIds })
            .then((res) => {
                const list = res.suggestions || [];
                setSuggestions(list);
                const all = {};
                list.forEach((s) => Object.keys(s.suggestions).forEach((f) => { all[`${s.row_id}|${f}`] = true; }));
                setPicked(all);
                setPhase(list.length ? 'review' : 'empty');
            })
            .catch((err) => { setError(lmError(err, 'AI enrichment is not available right now.')); setPhase('error'); });
    }, [open, api, rowIds]);

    const apply = async () => {
        const chosen = [];
        suggestions.forEach((s) => Object.entries(s.suggestions).forEach(([field, sug]) => {
            if (picked[`${s.row_id}|${field}`]) chosen.push({ row_id: s.row_id, field, value: sug.value });
        }));
        if (!chosen.length) return;
        setPhase('applying');
        try {
            const res = await api.enrichApply(chosen);
            const n = res.succeeded || 0;
            if (res.failed) {
                // e.g. a row left Staging meanwhile: say so rather than claim success.
                toast.warning(`Enriched ${n} record${n === 1 ? '' : 's'}; ${res.failed} could not be updated. Refresh and check them.`);
            } else {
                toast.success(`Enriched ${n} record${n === 1 ? '' : 's'} (staged values only)`);
            }
            onApplied?.();
            onClose?.();
        } catch (err) {
            setError(lmError(err, 'The suggestions could not be applied.'));
            setPhase('review');
        }
    };

    const pickedCount = Object.values(picked).filter(Boolean).length;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && phase !== 'applying' && onClose?.()}>
            <DialogContent className="sm:max-w-[640px]" data-testid="lm-enrich-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[17px] font-semibold text-foreground">
                        <WandSparkles className="w-4 h-4" aria-hidden="true" />Enrich staged records
                    </DialogTitle>
                    <DialogDescription className="text-[13px] text-muted-foreground">
                        AI suggestions for empty fields. Applied values stay in Staging; nothing changes in the CRM until Commit.
                    </DialogDescription>
                </DialogHeader>
                {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] bg-red-50 dark:bg-bad-bg text-[12.5px] text-red-800 dark:text-bad">{error}</div>}
                {phase === 'loading' && <div className="py-8 flex justify-center gap-2 text-[13px] text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Asking the AI…</div>}
                {phase === 'empty' && <p className="text-[13px] text-ink-2">No confident suggestions for the selected records.</p>}
                {(phase === 'review' || phase === 'applying') && (
                    <div className="max-h-[50vh] overflow-y-auto space-y-3">
                        {suggestions.map((s) => (
                            <div key={s.row_id} className="border border-border rounded-[10px] p-3">
                                <div className="text-[13px] font-semibold text-foreground">{labelFor(s.row_id)}</div>
                                {Object.entries(s.suggestions).map(([field, sug]) => (
                                    <label key={field} className="mt-1.5 flex items-start gap-2 text-[12.5px] text-foreground">
                                        <input type="checkbox" checked={!!picked[`${s.row_id}|${field}`]}
                                               onChange={(e) => setPicked((p) => ({ ...p, [`${s.row_id}|${field}`]: e.target.checked }))} />
                                        <span>
                                            <span className="font-medium">{labelOf(field, fieldLabels)}:</span> {sug.value}
                                            {sug.confidence != null && <span className="text-muted-foreground"> · {sug.confidence}%</span>}
                                            {sug.reasoning && <span className="block text-xs text-muted-foreground">{sug.reasoning}</span>}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <GhostButton onClick={onClose} disabled={phase === 'applying'}>Close</GhostButton>
                    {(phase === 'review' || phase === 'applying') && (
                        <PrimaryButton onClick={apply} disabled={phase === 'applying' || !pickedCount} testId="lm-enrich-apply"
                                       title={pickedCount ? undefined : 'Tick at least one suggestion'}>Apply selected</PrimaryButton>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
